/**
 * Admin → SpamGuard keywords — /admin/spam-keywords. Platform-only.
 *
 * Operator-loadable spam/fraud terms layered on top of the LLM classifier.
 * Each rule has a match type (word / substring / regex) and an action:
 *   - block  → match blocks the campaign outright (score 100)
 *   - review → match forces the campaign into the review queue
 *   - weight → match adds N points to the LLM score (tunable nudge)
 * Rules are global (apply to every tenant). Disable without deleting via
 * the Enabled toggle; the screener reads enabled rules only.
 */

import { type FormEvent, type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, Input, Skeleton } from '@/components/ui';
import {
  useCreateSpamKeyword,
  useDeleteSpamKeyword,
  useSpamKeywords,
  useUpdateSpamKeyword,
  type KeywordAction,
  type KeywordInput,
  type KeywordMatchType,
  type SpamKeyword,
} from '@/hooks/useSpamGuard';
import { ApiError } from '@/lib/api';

const MATCH_TYPES: KeywordMatchType[] = ['word', 'substring', 'regex'];
const ACTIONS: KeywordAction[] = ['block', 'review', 'weight'];

function actionBadge(a: KeywordAction): 'danger' | 'warn' | 'info' {
  if (a === 'block') return 'danger';
  if (a === 'review') return 'warn';
  return 'info';
}

interface FormState {
  term: string;
  match_type: KeywordMatchType;
  action: KeywordAction;
  weight: string;
  notes: string;
}

const INITIAL_FORM: FormState = {
  term: '',
  match_type: 'word',
  action: 'review',
  weight: '15',
  notes: '',
};

// Translate a FormState into the API body, attaching weight only when the
// action actually consumes it (the backend ignores it otherwise, but
// sending a clean payload keeps the contract obvious).
function toInput(f: FormState): KeywordInput {
  return {
    term: f.term.trim(),
    match_type: f.match_type,
    action: f.action,
    weight: f.action === 'weight' ? Number(f.weight) || 0 : null,
    notes: f.notes.trim() || undefined,
  };
}

interface RowProps {
  k: SpamKeyword;
  setError: (msg: string | null) => void;
}

function KeywordRow({ k, setError }: RowProps): JSX.Element {
  const { t } = useTranslation('default');
  const update = useUpdateSpamKeyword();
  const del = useDeleteSpamKeyword();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>({
    term: k.term,
    match_type: k.match_type,
    action: k.action,
    weight: String(k.weight ?? 15),
    notes: k.notes ?? '',
  });

  const busy = update.isPending || del.isPending;

  const wrap = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('sg_kw_err_generic'));
      return false;
    }
  };

  const toggleEnabled = () =>
    void wrap(() =>
      update.mutateAsync({
        id: k.id,
        input: {
          term: k.term,
          match_type: k.match_type,
          action: k.action,
          weight: k.weight,
          enabled: !k.enabled,
          notes: k.notes,
        },
      }),
    );

  const saveEdit = async () => {
    const ok = await wrap(() =>
      update.mutateAsync({ id: k.id, input: { ...toInput(form), enabled: k.enabled } }),
    );
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <tr className="border-b border-line last:border-0 bg-surface-2 align-top">
        <td className="px-5 py-3">
          <Input
            value={form.term}
            onChange={(e) => setForm((p) => ({ ...p, term: e.target.value }))}
            disabled={busy}
          />
        </td>
        <td className="px-5 py-3">
          <select
            value={form.match_type}
            onChange={(e) => setForm((p) => ({ ...p, match_type: e.target.value as KeywordMatchType }))}
            disabled={busy}
            className="h-8 px-2 text-xs bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
          >
            {MATCH_TYPES.map((m) => (
              <option key={m} value={m}>
                {t(`sg_kw_match_${m}`)}
              </option>
            ))}
          </select>
        </td>
        <td className="px-5 py-3">
          <div className="flex items-center gap-2">
            <select
              value={form.action}
              onChange={(e) => setForm((p) => ({ ...p, action: e.target.value as KeywordAction }))}
              disabled={busy}
              className="h-8 px-2 text-xs bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
            >
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {t(`sg_kw_action_${a}`)}
                </option>
              ))}
            </select>
            {form.action === 'weight' && (
              <Input
                type="number"
                value={form.weight}
                onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))}
                disabled={busy}
                className="w-16"
              />
            )}
          </div>
        </td>
        <td className="px-5 py-3" colSpan={2}>
          <Input
            value={form.notes}
            placeholder={t('sg_kw_notes_placeholder')}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            disabled={busy}
          />
        </td>
        <td className="px-5 py-3">
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" onClick={() => void saveEdit()} disabled={busy || !form.term.trim()}>
              {t('sg_kw_save')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
              {t('sg_cancel')}
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2 align-top">
      <td className="px-5 py-3 num text-text whitespace-nowrap">{k.term}</td>
      <td className="px-5 py-3 text-text-2">{t(`sg_kw_match_${k.match_type}`)}</td>
      <td className="px-5 py-3">
        <Badge variant={actionBadge(k.action)}>{t(`sg_kw_action_${k.action}`)}</Badge>
        {k.action === 'weight' && k.weight !== null && (
          <span className="text-[11px] text-text-4 ml-2 num">+{k.weight}</span>
        )}
      </td>
      <td className="px-5 py-3 text-text-3 max-w-[260px]">
        {k.notes ? k.notes : <span className="text-text-4">—</span>}
      </td>
      <td className="px-5 py-3">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={k.enabled}
            disabled={busy}
            onChange={toggleEnabled}
            className="w-4 h-4 rounded border border-line-strong accent-volt-500 disabled:opacity-50 cursor-pointer"
            aria-label={t('sg_kw_enabled')}
          />
          <span className="text-[12px] text-text-3">
            {k.enabled ? t('sg_kw_on') : t('sg_kw_off')}
          </span>
        </label>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)} disabled={busy}>
            {t('sg_kw_edit')}
          </Button>
          <Button
            size="sm"
            className="bg-danger text-white hover:bg-danger/90"
            onClick={() => void wrap(() => del.mutateAsync(k.id))}
            disabled={busy}
          >
            {t('sg_kw_delete')}
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminSpamKeywordsPage(): JSX.Element {
  const { t } = useTranslation('default');
  const list = useSpamKeywords();
  const create = useCreateSpamKeyword();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const items = list.data?.items ?? [];

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateError(null);
    if (!form.term.trim()) {
      setCreateError(t('sg_kw_term_required'));
      return;
    }
    try {
      await create.mutateAsync(toInput(form));
      setForm(INITIAL_FORM);
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : t('sg_kw_err_generic'));
    }
  };

  const busy = create.isPending;

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('sg_kw_title')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">{t('sg_kw_subtitle')}</p>
      </header>

      <Card padding="lg" className="mb-6">
        <h2 className="display text-[16px] font-semibold text-text mb-4">{t('sg_kw_add_title')}</h2>
        {createError && (
          <div role="alert" className="px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-4 text-[13px] text-danger">
            {createError}
          </div>
        )}
        <form onSubmit={handleCreate} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div className="md:col-span-2">
              <label htmlFor="kw-term" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('sg_kw_field_term')}
              </label>
              <Input
                id="kw-term"
                placeholder={t('sg_kw_term_placeholder')}
                value={form.term}
                onChange={(e) => setForm((p) => ({ ...p, term: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="kw-match" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('sg_kw_field_match')}
              </label>
              <select
                id="kw-match"
                value={form.match_type}
                onChange={(e) => setForm((p) => ({ ...p, match_type: e.target.value as KeywordMatchType }))}
                disabled={busy}
                className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
              >
                {MATCH_TYPES.map((m) => (
                  <option key={m} value={m}>
                    {t(`sg_kw_match_${m}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="kw-action" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('sg_kw_field_action')}
              </label>
              <select
                id="kw-action"
                value={form.action}
                onChange={(e) => setForm((p) => ({ ...p, action: e.target.value as KeywordAction }))}
                disabled={busy}
                className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(`sg_kw_action_${a}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="kw-weight" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('sg_kw_field_weight')}
              </label>
              <Input
                id="kw-weight"
                type="number"
                value={form.weight}
                onChange={(e) => setForm((p) => ({ ...p, weight: e.target.value }))}
                disabled={busy || form.action !== 'weight'}
                placeholder="15"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <label htmlFor="kw-notes" className="block text-[12px] font-medium text-text-2 mb-1.5">
                {t('sg_kw_field_notes')}
              </label>
              <Input
                id="kw-notes"
                placeholder={t('sg_kw_notes_placeholder')}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                disabled={busy}
              />
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? t('sg_kw_adding') : t('sg_kw_add_button')}
            </Button>
          </div>
        </form>
        <p className="text-[12px] text-text-4 mt-3">{t('sg_kw_add_help')}</p>
      </Card>

      <Card padding="none">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('sg_kw_list_title', { count: items.length })}
          </h2>
        </div>

        {actionError && (
          <div className="px-5 py-2 bg-danger-bg border-b border-danger-bd text-[13px] text-danger">
            {actionError}
          </div>
        )}

        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">{t('sg_kw_load_error')}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void list.refetch()}>
              {t('sg_retry')}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('sg_kw_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1">{t('sg_kw_empty_help')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('sg_kw_col_term')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_kw_col_match')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_kw_col_action')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_kw_col_notes')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('sg_kw_col_enabled')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('sg_kw_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <KeywordRow key={k.id} k={k} setError={setActionError} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
